import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";


const industries = [
  {
    id: "education",
    badge: "SmartClass AI",
    title: "Education",
    tagline: "AI-powered learning that adapts to every student",
    description:
      "Transform how institutions teach and students learn with intelligent systems that personalize education, automate assessments, and provide real-time analytics.",
    image:
      "https://firebasestorage.googleapis.com/v0/b/classroom-c0e76.firebasestorage.app/o/cal-web%2Fsc.jpeg?alt=media&token=3e99cc55-5980-4c61-8fd4-88db967b7c73",
    problems: [
      "Student engagement decline",
      "Learning personalization at scale",
      "Assessment automation",
      "Performance tracking gaps",
    ],
    solutions: [
      "AI Tutors & Learning Assistants",
      "AI Practice Test Generators",
      "Student Analytics Dashboards",
      "Intelligent Learning Management",
    ],
    outcome:
      "Improve learning outcomes by 40% while reducing operational overhead and administrative burden.",
    cta: "Explore Education Platform",
    link: "/education",
    color: "#3B82F6",
  },
  {
    id: "healthcare",
    badge: "HealthCare AI",
    title: "Healthcare",
    tagline: "Intelligent healthcare operations and patient engagement",
    description:
      "Empower healthcare providers with AI systems that streamline scheduling, automate follow-ups, and enhance patient communication — so clinicians can focus on care.",
    image:
      "https://firebasestorage.googleapis.com/v0/b/classroom-c0e76.firebasestorage.app/o/cal-web%2Fht.jpeg?alt=media&token=21c7fd6e-dfd4-421f-a403-71d558acc064",
    problems: [
      "Appointment scheduling overload",
      "Patient engagement gaps",
      "Follow-up management",
      "Administrative workload",
    ],
    solutions: [
      "AI Patient Assistants",
      "Appointment Automation Systems",
      "Healthcare Workflow Agents",
      "Patient Communication Platforms",
    ],
    outcome:
      "Improve patient experience scores by 60% and reduce no-show rates by 35% through intelligent automation.",
    cta: "Explore Healthcare Solutions",
    link: "/healthcare",
    color: "#EC4899",
  },
  {
    id: "real-estate",
    badge: "PropTech AI",
    title: "Real Estate",
    tagline: "Convert more leads and close deals faster with AI",
    description:
      "Give your real estate team an AI-powered edge — qualify leads 24/7, automate site visit bookings, and follow up intelligently via voice and WhatsApp.",
    image:
      "https://firebasestorage.googleapis.com/v0/b/classroom-c0e76.firebasestorage.app/o/cal-web%2Fpt.jpeg?alt=media&token=2c4f7d93-b98a-442b-8a2b-a45fafe3fe01",
    problems: [
      "Lead qualification bottlenecks",
      "Property inquiry overflow",
      "Site visit booking friction",
      "Follow-up automation gaps",
    ],
    solutions: [
      "AI Voice Agents for Leads",
      "WhatsApp Sales Automation",
      "Lead Qualification AI Agents",
      "End-to-End Sales Automation",
    ],
    outcome:
      "Generate 3x more qualified site visits and increase conversion rates by 50% through 24/7 AI engagement.",
    cta: "Explore PropTech Platform",
    link: "/real-estate",
    color: "#F59E0B",
  },
  {
    id: "financial",
    badge: "FinTech AI",
    title: "Financial Services",
    tagline: "Scale customer service and reduce costs with AI",
    description:
      "Deploy intelligent AI advisors and support agents that handle onboarding, lead nurturing, and customer queries — freeing your team for complex, high-value work.",
    image:
      "https://firebasestorage.googleapis.com/v0/b/classroom-c0e76.firebasestorage.app/o/cal-web%2Fft.jpeg?alt=media&token=9bb921fd-2c2a-44fe-a08b-230d5e69a1c8",
    problems: [
      "Customer onboarding friction",
      "Lead nurturing at scale",
      "Support cost escalation",
      "Customer retention challenges",
    ],
    solutions: [
      "AI Financial Advisors",
      "AI Customer Support Agents",
      "Financial Workflow Automation",
      "Client Engagement Platforms",
    ],
    outcome:
      "Reduce support costs by 45% and improve customer satisfaction scores while scaling operations efficiently.",
    cta: "Explore FinTech Platform",
    link: "/financial-services",
    color: "#8B5CF6",
  },
  {
    id: "retail",
    badge: "Commerce AI",
    title: "Retail & Commerce",
    tagline: "Turn browsers into buyers with intelligent AI assistance",
    description:
      "Boost conversions and customer loyalty with AI shopping assistants, intelligent recommendations, and WhatsApp commerce that meets customers where they are.",
    image:
      "https://firebasestorage.googleapis.com/v0/b/classroom-c0e76.firebasestorage.app/o/cal-web%2Fcm.jpeg?alt=media&token=be7149d5-676a-40f3-bf05-307a909111cb",
    problems: [
      "Customer support volume",
      "Product discovery friction",
      "Cart abandonment rates",
      "Low customer engagement",
    ],
    solutions: [
      "AI Shopping Assistants",
      "WhatsApp Commerce Agents",
      "Recommendation Engines",
      "AI Customer Retention Systems",
    ],
    outcome:
      "Increase conversion rates by 35% and boost customer lifetime value through personalized AI experiences.",
    cta: "Explore Commerce Platform",
    link: "/retail",
    color: "#F97316",
  },
  {
    id: "hospitality",
    badge: "Restro One AI",
    title: "Hospitality & Restaurants",
    tagline: "Never miss a booking or a customer call again",
    description:
      "Let AI handle reservations, answer calls, take orders via WhatsApp, and manage CRM — so your staff can focus on delivering exceptional experiences.",
    image:
      "https://firebasestorage.googleapis.com/v0/b/classroom-c0e76.firebasestorage.app/o/cal-web%2Fres.jpeg?alt=media&token=40201bd1-c849-497f-8df9-b44193a0c021",
    problems: [
      "Missed reservations & calls",
      "Manual ordering processes",
      "Low repeat customer rate",
      "Staff overwhelm at peak hours",
    ],
    solutions: [
      "AI Reservation Agents",
      "AI Voice Receptionist",
      "WhatsApp Ordering Systems",
      "CRM & Loyalty Automation",
    ],
    outcome:
      "Increase bookings by 60%, eliminate missed calls, and build a loyal customer base with automated CRM.",
    cta: "Explore Restaurant Platform",
    link: "/hospitality",
    color: "#EF4444",
  },
];

const IndustryCard = ({ industry, index, inView }) => {
  const isReversed = index % 2 !== 0;

  return (
    <div
      data-testid={`industry-card-${industry.id}`}
      className={`flex flex-col ${isReversed ? "lg:flex-row-reverse" : "lg:flex-row"} overflow-hidden rounded-2xl border border-white/6 mb-6 transition-all duration-700 ${
        inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      }`}
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      {/* Image Side */}
      <div className="industry-card-image lg:w-[42%] h-64 lg:h-auto min-h-[300px]">
        <img
          src={industry.image}
          alt={industry.title}
          loading="lazy"
          className="w-full h-full"
          style={{ objectFit: "cover" }}
        />
        <div
          className="absolute inset-0"
          style={{
            background: isReversed
              ? "linear-gradient(to left, rgba(5,5,5,0.5) 0%, transparent 60%)"
              : "linear-gradient(to right, rgba(5,5,5,0.5) 0%, transparent 60%)",
          }}
        />
        {/* Badge overlay */}
        <div className="absolute top-5 left-5">
          <span className="px-3 py-1.5 rounded-full text-xs font-bold bg-black/60 border border-white/20 text-white backdrop-blur-sm">
            {industry.badge}
          </span>
        </div>
      </div>

      {/* Content Side */}
      <div className="lg:w-[58%] bg-[#0A0A0A] p-8 md:p-10 lg:p-14 flex flex-col justify-center">
        <div className="mb-2">
          <span
            className="text-xs font-bold tracking-widest uppercase"
            style={{ color: industry.color }}
          >
            {industry.badge}
          </span>
        </div>
        <h3
          className="text-3xl md:text-4xl font-bold text-white mb-3"
          style={{ fontFamily: "Outfit, sans-serif" }}
        >
          {industry.title}
        </h3>
        <p className="text-[#00E599] font-medium text-sm mb-4">{industry.tagline}</p>
        <p className="text-[#A1A1AA] text-sm leading-relaxed mb-8">{industry.description}</p>

        {/* Two columns: Problems & Solutions */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
          <div>
            <h4 className="text-xs font-bold text-[#00E599] uppercase tracking-widest mb-3">
              Problems Solved
            </h4>
            <ul className="space-y-2">
              {industry.problems.map((p, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm text-[#A1A1AA]">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#00E599]/60 flex-shrink-0" />
                  {p}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="text-xs font-bold text-[#00E599] uppercase tracking-widest mb-3">
              AI Solutions
            </h4>
            <ul className="space-y-2">
              {industry.solutions.map((s, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm text-[#A1A1AA]">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#00E599]/60 flex-shrink-0" />
                  {s}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Outcome */}
        <div className="p-4 rounded-xl bg-[#00E599]/5 border border-[#00E599]/12 mb-8">
          <p className="text-sm text-[#A1A1AA]">
            <span className="font-semibold text-[#00E599]">Outcome: </span>
            {industry.outcome}
          </p>
        </div>

        {/* CTA */}
        <Link
          to={industry.link}
          data-testid={`industry-cta-${industry.id}`}
          className="inline-flex items-center gap-2 bg-[#00E599] text-black font-semibold text-sm rounded-full px-6 py-3 hover:bg-[#00ff99] transition-all duration-200 w-fit hover:shadow-[0_0_20px_rgba(0,229,153,0.3)]"
        >
          {industry.cta}
          <ArrowRight size={15} />
        </Link>
      </div>
    </div>
  );
};

const Industries = () => {
  const sectionRef = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.05 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="industries"
      data-testid="industries-section"
      ref={sectionRef}
      className="py-24 md:py-32 bg-[#050505]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div className="mb-16">
          <span className="text-xs font-bold text-[#00E599] uppercase tracking-widest block mb-4">
            Vertical AI Platforms
          </span>
          <h2
            className="text-4xl md:text-5xl font-bold text-white mb-5"
            style={{ fontFamily: "Outfit, sans-serif", letterSpacing: "-0.025em" }}
          >
            Industries We Transform
          </h2>
          <p className="text-[#A1A1AA] text-lg max-w-2xl leading-relaxed">
            We've built deep expertise across six major industry verticals — each with purpose-built AI
            systems designed for real business outcomes.
          </p>
        </div>

        {/* Industry Cards */}
        <div>
          {industries.map((industry, index) => (
            <IndustryCard
              key={industry.id}
              industry={industry}
              index={index}
              inView={inView}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Industries;
